import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { map, filter, reduce, findIndex, cloneDeep, first } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import ReactResizeDetector from 'react-resize-detector';
import SearchField from '../../../../components/search-field';
import ProgressBar from '../../../../components/progress-bar';
// Import Actions
import { 
  requestFetchOwnerFields,
  requestUpdateOwnerFields, 
} from '../../SetupActions';
import Pusher from '../../../../components/pusher';
import NumberDisplay from '../../../../components/number-display';

// Import Selectors
import { getViewableDimensions } from '../../../App/AppReducer';
import DataTypeCellRenderer from '../../components/cells/DataTypeCellRenderer';
import ToggleCellRenderer from '../../components/cells/ToggleFieldRenderer';
import APICellRenderer from '../../components/cells/APICellRenderer';
import FieldLabelCellRenderer from '../../components/cells/FieldLabelCellRenderer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';

class OwnerPagePage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      error: null,
      allUpdating: false,
      fields: [],
      dirty: false,
      navBarHeight: 0,
      socketId: null,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.markAsOwnerField = this.markAsOwnerField.bind(this);
    this.submit = this.submit.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
    this.onSocketUpdate = this.onSocketUpdate.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onResizNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }
  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  loadFields(rawFields) {
    return reduce(rawFields || [], (items, field) => {
      if (field.sync_required === true || field.is_owner_field === true) {
        const f = cloneDeep(field);
        f.original_is_owner_field = f.is_owner_field;
        items.push(f);
      }
      return items;
    }, []);
  }

  onSocketUpdate(e) {
    const { id, isError, message, modified } = e;
    if (id === this.state.socketId) {
      if (isError) {
        const el = (<div>{message}</div>);
        this.context.addNotification(el, { type: 'error', autoClose: true });
      } else {
        const el = (<div>Updated <NumberDisplay value={modified} precision={0} suffix="record" suffixIsUnit /> successfully!</div>);
        this.context.addNotification(el, { type: 'success', autoClose: true });
      }
      this.setState({ socketId: null });
    }
  }

  loadCustomFields() {
    this.setState({
      loading: true, error: null, loaded: false, fields: [],
    });
    const allFieldsPromise = this.props.dispatch(requestFetchOwnerFields());
    allFieldsPromise.then(
      (results) => {
        // clone!!!!! results.fields
        const fields = this.loadFields(results.fields);
        this.setState({
          loading: false, error: null, loaded: true, fields,
        });
      },
      (error) => {
        this.setState({
          loading: false, error, loaded: false, fields: [],
        });
      },
    );
    return allFieldsPromise;
  }

  markAsOwnerField(field) {
    const { fields } = this.state;
    const newFields = fields.map((f) => {
      if (f._id === field._id) {
        return { ...f, is_owner_field: true };
      }
      return { ...f, is_owner_field: false };
    });
    const dirtyFields = filter(newFields, f => f.original_is_owner_field !== f.is_owner_field);
    const dirty = dirtyFields.length > 0;
    this.setState({ dirty, fields: newFields }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  submit() {
    const { fields, allUpdating } = this.state;
    if (allUpdating) {
      return;
    }
    this.setState({ allUpdating: true, socketId: null });
    const selectedField = first(map(filter(fields, { is_owner_field: true }), 'name'));
    this.props.dispatch(requestUpdateOwnerFields({ owner_field: selectedField }))
      .then(
        (results) => {
          const newFields = this.loadFields(results.fields);
          this.setState({ allUpdating: false, fields: newFields, dirty: false, socketId: results.socket_id });
        },
        () => {
          this.setState({ allUpdating: false });
        },
      );
  }

  renderInstruction(orgType) {
    let opportunityObjectName = 'Opportunity';
    if (orgType === 'hubspot') {
      opportunityObjectName = 'deal';
    }

    return (
      <div className="well well-sm">
        <div>
          <p>

            If you are not using the default Opportunity Owner field and are using a custom field for the Opportunity Owner, 
            select the appropriate custom field below. 
          </p>
          <p>
            If you do not see the correct custom field, ensure that you have enabled the field in <Link to={`/setup/sync-settings/${opportunityObjectName}`}>Sync Settings</Link>. 
            If the field has not been enabled in <Link to={`/setup/sync-settings/${opportunityObjectName}`}>Sync Settings</Link> it will not be displayed as an option here.
          </p>
        </div>
      </div>
    );
  }


  renderTable() {
    const { viewableDimensions } = this.props;
    const {
      fields, allUpdating, dirty,
      navBarHeight, socketId,
    } = this.state;

    if (fields.length === 0) {
      return (
        <div className="alert alert-danger">
          No custom fields of type <code>currency</code> found.
        </div>
      );
    }

    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        suppressMenu: true,
        width: 450,
        suppressMovable: true,
        cellRenderer: FieldLabelCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'API Name',
        field: 'name',
        suppressMenu: true,
        width: 250,
        cellRenderer: APICellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        suppressMenu: true,
        width: 280,
        suppressMovable: true,
        cellRenderer: DataTypeCellRenderer,
      },
      {
        headerName: 'Use as Owner Field',
        field: 'is_owner_field',
        suppressMenu: true,
        width: 200,
        suppressMovable: true,
        cellRenderer: ToggleCellRenderer,
        cellRendererParams: {
          onToggle: this.markAsOwnerField,
          disabled: allUpdating || socketId !== null,
          isRadio: true,
        },
      },  
    ];

    const viewableHeight = viewableDimensions.height - navBarHeight;
    return (
      <div>

        {fields.length > 0 && 
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
            {dirty &&
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5 }}>
                <button onClick={this.submit} disabled={(allUpdating || socketId !== null)} className="btn btn-primary">
                  Save &amp; Update
                </button>
              </div>
            }
            {socketId !== null &&
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5, marginTop: 25 }}>
                <div><em>Updating all records...</em></div>
                <Pusher
                  event={`owner-id-updated-${socketId}`}
                  handler={this.onSocketUpdate}
                />
              </div>
            }
            <ReactResizeDetector handleHeight onResize={this.onResizNavBar} />
          </nav>
        }
        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={fields}
          />
        </div>
      </div>
    );
  }


  render() {
    const { loading, loaded } = this.state;
    return (
      <div>
        <Helmet title="Manage Opportunity Owner Field" />
        <div className="container">
          {loading && <ProgressBar />}
          {!loading && loaded && this.renderTable()}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    viewableDimensions: getViewableDimensions(state),
  };
}

OwnerPagePage.propTypes = {
  organization: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  viewableDimensions: PropTypes.shape({}).isRequired,
};

OwnerPagePage.contextTypes = {
  router: PropTypes.shape({}),
  addNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(OwnerPagePage);
